import React, { memo, useEffect, useRef } from 'react';
import { ExportOutlined } from '@ant-design/icons';
import clsx from 'clsx';
import TruncatedText from '../../../../shared/components/TruncatedText';
import type { WorkOrderListItem } from '../../../../types/InfrastructureManagerTypes';
import { useWorkOrderSettings } from '../../../../hooks/useWorkOrderSettings';
import { isLightColor } from '../workOrderList.utils';
import styles from './WorkOrderCalendar.module.scss';

const LINK_OUT_ICON = <ExportOutlined aria-hidden />;

interface CalendarPillProps {
  order: WorkOrderListItem;
  isSelected: boolean;
  onSelect: (id: number) => void;
  onExpand: (id: number) => void;
}

const CalendarPill = ({ order, isSelected, onSelect, onExpand }: CalendarPillProps) => {
  const { settings } = useWorkOrderSettings();
  const color = settings.statuses[order.status].color;

  const pillRef = useRef<HTMLLIElement>(null);

  // Scrolls only the tile's pill list (never the page) so a selection made elsewhere is visible.
  useEffect(() => {
    const pill = pillRef.current;
    const list = pill?.parentElement;
    if (!isSelected || !pill || !list) return;
    const pillRect = pill.getBoundingClientRect();
    const listRect = list.getBoundingClientRect();
    if (pillRect.top < listRect.top) list.scrollTop -= listRect.top - pillRect.top;
    else if (pillRect.bottom > listRect.bottom) list.scrollTop += pillRect.bottom - listRect.bottom;
  }, [isSelected]);

  const handleSelect = () => onSelect(order.id);
  const handleExpand = () => onExpand(order.id);

  const pillClassName = clsx(styles.calendarPill, {
    [styles.calendarPillLight]: color && isLightColor(color),
    [styles.calendarPillSelected]: isSelected,
  });

  return (
    <li ref={pillRef} className={pillClassName} style={{ '--wo-pill-bg': color } as React.CSSProperties}>
      <button
        type="button"
        className={styles.calendarPillExpand}
        aria-label={`Open ${order.title} details`}
        onClick={handleExpand}
      >
        {LINK_OUT_ICON}
      </button>
      <button type="button" className={styles.calendarPillTitle} aria-pressed={isSelected} onClick={handleSelect}>
        <TruncatedText text={order.title} />
      </button>
    </li>
  );
};

export default memo(CalendarPill);
